import type { HTMLAttributes } from 'react';

import classNames from 'classnames';

import styles from './Progress.module.css';

import { calculateActiveBarWidth } from './utils/calculateActiveBarWidth';

export type ProgressVariant = 'default' | 'bold';

export interface ProgressBaseProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'role' | 'aria-valuemin' | 'aria-valuemax' | 'aria-valuenow'
> {
  /**
   * The role of the progress bar. Use `status` when there's no visible label to
   * associate to the progress bar.
   */
  role: 'progressbar' | 'status';
  /** The number of steps represented by the progress bar. */
  steps: number;
  /**
   * The active step of the progress bar.
   *
   * @defaultValue 0
   */
  activeStep?: number;
  /**
   * The "done" percentage of the active step.
   *
   * @defaultValue 0
   */
  stepProgress?: number;
  /** Change the appearance of the progress bar. */
  variant?: ProgressVariant;
}

export type ProgressProps = ProgressBaseProps &
  (
    | {
        role: 'progressbar';
        /**
         * An id is required when role is `progressbar`. Assign this id to a
         * proper `label`:
         *
         * ```
         * <label htmlFor="progress">
         * <Progress id="progress" ... />
         * ```
         */
        id: string;
        /** A title is required when role is `progressbar`. */
        title: string;
      }
    | {
        role: 'status';
        /* An ARIA label is required when role="status". */
        ['aria-label']: string;
      }
  );
/**
 * Render a progress bar or a status bar to indicate the progress of a task.
 *
 * @see https://corgi-x.tfd.engineering/components/progress
 */
export function Progress(props: ProgressProps) {
  const {
    className,
    steps,
    activeStep = 0,
    stepProgress = 0,
    role,
    variant = 'default',
    ...divAttributes
  } = props;
  const barWidth = calculateActiveBarWidth(steps, activeStep, stepProgress);

  return (
    <div
      {...divAttributes}
      role={role}
      aria-valuemin={role === 'progressbar' ? 0 : undefined}
      aria-valuemax={role === 'progressbar' ? steps : undefined}
      aria-valuenow={role === 'progressbar' ? activeStep : undefined}
      className={classNames(className, styles.root, styles[variant])}
    >
      <div className={styles.background_bar} />
      <div className={styles.content}>
        {/* This "inactive" bar will be covered by the active one once the progress advances  */}
        <div className={styles.bar} />
        <div
          className={classNames(styles.bar, styles.bar_active)}
          style={{ width: `${barWidth}%` }}
        />
        <div className={styles.dots}>
          {[...(new Array(steps) as null[])].map((_, stepIndex) => (
            <span
              key={stepIndex}
              className={classNames(styles.dot, {
                [styles.dot_active]: activeStep && stepIndex < activeStep,
              })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
